import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useLeadDictionaries } from "@/features/leads/api/leads";
import {
  useUpdateCandidate,
  type CandidatePatch,
  type SearchCandidate,
} from "@/features/leads/api/searches";

/**
 * Podgląd i poprawka kandydata przed dodaniem do bazy.
 *
 * Człowiek zwykle wie więcej niż wyszukiwarka: dopisze maila ze stopki, poprawi
 * nazwę, wklei NIP z faktury. Po zapisie serwer przelicza weryfikację NIP-u i
 * kolizje — poprawka nie omija zabezpieczeń, tylko je karmi lepszymi danymi.
 *
 * PO AKCEPCIE to już tylko podgląd. Dane firmy zostały wtedy skopiowane do bazy
 * leadów i nic stąd do nich nie wraca, więc zapis byłby obietnicą bez pokrycia —
 * serwer go odrzuca, a my odsyłamy na kartę leada, gdzie poprawka coś zmienia.
 */

function FieldSource({
  source,
}: {
  source?: { origin: "registry" | "krs" | "web"; label: string; url?: string };
}) {
  if (!source) return null;
  const official = source.origin !== "web";
  const text = official ? `źródło: ${source.label} (urzędowe)` : `źródło: ${source.label}`;
  return source.url ? (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      title={source.url}
      className={
        official
          ? "mt-0.5 block text-[10px] text-emerald-600 hover:underline"
          : "mt-0.5 block text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:underline"
      }
    >
      {text}
    </a>
  ) : (
    <span
      className={
        official
          ? "mt-0.5 block text-[10px] text-emerald-600"
          : "mt-0.5 block text-[10px] text-[var(--color-text-muted)]"
      }
    >
      {text}
    </span>
  );
}

export function CandidateEditModal({
  candidate,
  onClose,
}: {
  candidate: SearchCandidate | null;
  onClose: () => void;
}) {
  const { data: dictionaries } = useLeadDictionaries();
  const update = useUpdateCandidate();
  const promoted = !!candidate?.lead;
  const [form, setForm] = useState<CandidatePatch>({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!candidate) return;
    setError("");
    setForm({
      name: candidate.name,
      nip: candidate.nip,
      city: candidate.city,
      voivodeship: candidate.voivodeship,
      website: candidate.website,
      email: candidate.email,
      phone: candidate.phone,
      contact_person: candidate.contact_person,
      rationale: candidate.rationale,
    });
  }, [candidate]);

  const submit = () => {
    if (!candidate) return;
    if (!form.name?.trim()) return setError("Nazwa firmy jest wymagana.");
    setError("");
    update.mutate(
      { id: candidate.id, ...form },
      {
        onSuccess: onClose,
        onError: (err: unknown) => {
          const errors = (err as { response?: { data?: { errors?: Record<string, unknown> } } })
            .response?.data?.errors;
          const detail = errors && Object.values(errors)[0];
          setError(Array.isArray(detail) ? String(detail[0]) : "Nie udało się zapisać.");
        },
      },
    );
  };

  return (
    <Modal
      open={!!candidate}
      onClose={onClose}
      title={candidate ? `Kandydat — ${candidate.name}` : ""}
      size="lg"
    >
      {candidate && (
        <div className="space-y-3">
          {promoted && (
            <p className="border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-2 text-xs text-[var(--color-text-secondary)]">
              Ta firma jest już w bazie leadów — poniżej został zapis tego, co zwróciło
              wyszukiwanie, i poprawki nie dotrą do jej karty.{" "}
              <Link to={`/leads/${candidate.lead}`} className="text-[var(--color-primary)] underline">
                Popraw dane na karcie leada
              </Link>
              .
            </p>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="section-label mb-1 block">Nazwa firmy</label>
              <input
                className="input-base"
                disabled={promoted}
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="section-label mb-1 block">
                NIP{" "}
                {candidate.nip_verified && (
                  <span className="text-emerald-600">
                    <ShieldCheck size={10} className="inline" /> w rejestrze
                  </span>
                )}
              </label>
              <input
                className="input-base"
                disabled={promoted}
                value={form.nip ?? ""}
                onChange={(e) => setForm({ ...form, nip: e.target.value })}
              />
              <FieldSource source={candidate.field_sources?.nip} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="section-label mb-1 block">Miejscowość</label>
              <input
                className="input-base"
                disabled={promoted}
                value={form.city ?? ""}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
              <FieldSource source={candidate.field_sources?.city} />
            </div>
            <div>
              <label className="section-label mb-1 block">Województwo</label>
              <select
                className="input-base"
                disabled={promoted}
                value={form.voivodeship ?? ""}
                onChange={(e) => setForm({ ...form, voivodeship: e.target.value })}
              >
                <option value="">—</option>
                {(dictionaries?.voivodeships ?? []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="section-label mb-1 block">Strona WWW</label>
              <input
                className="input-base"
                disabled={promoted}
                value={form.website ?? ""}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
              <FieldSource source={candidate.field_sources?.website} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="section-label mb-1 block">Osoba kontaktowa</label>
              <input
                className="input-base"
                disabled={promoted}
                value={form.contact_person ?? ""}
                onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
              />
              <FieldSource source={candidate.field_sources?.contact_person} />
            </div>
            <div>
              <label className="section-label mb-1 block">E-mail</label>
              <input
                className="input-base"
                disabled={promoted}
                value={form.email ?? ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <FieldSource source={candidate.field_sources?.email} />
            </div>
            <div>
              <label className="section-label mb-1 block">Telefon</label>
              <input
                className="input-base"
                disabled={promoted}
                value={form.phone ?? ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <FieldSource source={candidate.field_sources?.phone} />
            </div>
          </div>

          <div>
            <label className="section-label mb-1 block">Dlaczego pasuje</label>
            <textarea
              className="input-base min-h-[4rem]"
              disabled={promoted}
              value={form.rationale ?? ""}
              onChange={(e) => setForm({ ...form, rationale: e.target.value })}
            />
          </div>

          {candidate.sources.length > 0 && (
            <div>
              <span className="section-label">Źródła</span>
              <ul className="mt-1 space-y-0.5">
                {candidate.sources.map((url) => (
                  <li key={url}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                    >
                      <ExternalLink size={10} /> {url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {candidate.enrichment_log && (
            <div>
              <span className="section-label">Przebieg doszukiwania</span>
              <pre className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap border border-[var(--color-border)] p-2 text-[11px] leading-relaxed text-[var(--color-text-secondary)]">
                {candidate.enrichment_log}
              </pre>
            </div>
          )}

          {error && <p className="text-sm text-[var(--color-primary)]">{error}</p>}

          <div className="flex justify-end gap-2 border-t border-[var(--color-border)] pt-3">
            <Button variant="ghost" onClick={onClose}>
              Zamknij
            </Button>
            {!promoted && (
              <Button onClick={submit} loading={update.isPending}>
                Zapisz poprawki
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
